/**
 * @fileoverview Command palette — Foundry Quicksearch chrome, IronCase data.
 *
 * Uses our own quicksearch/ primitives (QuicksearchDialog + QuicksearchInput
 * + QuicksearchFilters + QuicksearchCategoryTag + QuicksearchSection +
 * QuicksearchResultItem + QuicksearchFooter) instead of Blueprint's Omnibar.
 *
 * Matches the Foundry "Quicksearch" reference: a flat 45px input bar, a Filter
 * pill row (Cases / Pages / Actions — clicking a pill narrows results by kind),
 * a "Recent searches" section rendered in the initial/empty state (backed by a
 * small localStorage list, with a representative fallback so it's never blank),
 * and a darkest hotkeys footer. Typing filters routes/actions/cases as before.
 *
 * Wired to Cmd/Ctrl+J. Cmd/Ctrl+K is reserved for in-context search.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { useNavigate } from "react-router";
import { Icon, Tag, useHotkeys, type HotkeyConfig, type IconName } from "@blueprintjs/core";
import { useQueryClient } from "@tanstack/react-query";

import { useCases } from "../lib/hooks";
import { useAuth } from "../contexts/auth-context";
import {
  QuicksearchDialog,
  QuicksearchInput,
  QuicksearchFilters,
  QuicksearchCategoryTag,
  QuicksearchSection,
  QuicksearchResultItem,
  QuicksearchFooter,
} from "./quicksearch";

type CommandKind = "case" | "route" | "action";

interface Command {
  id: string;
  kind: CommandKind;
  label: string;
  /** Secondary text — case number, route path, etc. */
  hint?: string;
  icon: IconName;
  /** Keyword string used by the predicate; defaults to label + hint. */
  keywords?: string;
  onSelect: () => void | Promise<void>;
}

const KIND_LABEL: Record<CommandKind, string> = {
  route: "Go to",
  action: "Actions",
  case: "Cases",
};
const KIND_ORDER: CommandKind[] = ["route", "action", "case"];

/** Filter pills shown above the results; each toggles a kind narrow. */
const FILTER_PILLS: ReadonlyArray<{ kind: CommandKind; label: string }> = [
  { kind: "case", label: "Cases" },
  { kind: "route", label: "Pages" },
  { kind: "action", label: "Actions" },
];

const RECENT_KEY = "ic-quicksearch-recent";
const RECENT_CAP = 5;
/** Shown when the user has no stored history yet, so the section is never blank. */
const RECENT_FALLBACK: ReadonlyArray<string> = ["Workspace", "Cases", "Flagged cases"];

/** Read the recent-search history from localStorage (SSR/Workers-safe). */
function readRecent(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string").slice(0, RECENT_CAP);
  } catch {
    return [];
  }
}

/** Push a term to the front of the recent-search history (deduped, capped). */
function pushRecent(term: string): void {
  if (typeof window === "undefined") return;
  const trimmed = term.trim();
  if (!trimmed) return;
  try {
    const next = [trimmed, ...readRecent().filter((t) => t !== trimmed)].slice(0, RECENT_CAP);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* localStorage unavailable — recent history is best-effort. */
  }
}

export function CommandPalette(): ReactElement | null {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [kindFilter, setKindFilter] = useState<CommandKind | null>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut } = useAuth();
  const { data: cases = [] } = useCases();

  // Cmd/Ctrl+J toggles the palette. Registered as a Blueprint *global* hotkey:
  // `useHotkeys` binds global combos to the document itself, so the handler
  // fires anywhere — no manual window.addEventListener needed. Registering it
  // here also (a) routes it through the shared HotkeysProvider that
  // BlueprintProvider mounts and (b) auto-populates the built-in hotkeys help
  // dialog (opened with `?`); the `label`/`group` below are what that dialog
  // reads. `allowInInput` keeps ⌘J working while focus is in any text field.
  // Cmd/Ctrl+K is reserved for in-context search.
  const hotkeys = useMemo<HotkeyConfig[]>(
    () => [
      {
        combo: "mod+j",
        label: "Toggle command palette (Quicksearch)",
        group: "Global",
        global: true,
        allowInInput: true,
        preventDefault: true,
        onKeyDown: () => setIsOpen((o) => !o),
      },
    ],
    [],
  );
  useHotkeys(hotkeys);

  // Launchers (header SearchTrigger, home hero search) open the palette by
  // dispatching an `iron:open-search` event — NOT by synthesizing a ⌘J
  // KeyboardEvent. Blueprint's useHotkeys ignores synthetic key events, so the
  // old `window.dispatchEvent(new KeyboardEvent(...))` trick silently no-op'd
  // once ⌘J moved to useHotkeys. Mirrors the `iron:open-application-portal`
  // pattern used elsewhere.
  useEffect(() => {
    const open = (): void => setIsOpen(true);
    window.addEventListener("iron:open-search", open);
    return () => window.removeEventListener("iron:open-search", open);
  }, []);

  // Reset query + filter + active index, and refresh recent history, on open.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIndex(0);
      setKindFilter(null);
      setRecent(readRecent());
    }
  }, [isOpen]);

  const close = useCallback(() => setIsOpen(false), []);

  const commands = useMemo<Command[]>(() => {
    const routes: Command[] = [
      {
        id: "route:dashboard",
        kind: "route",
        label: "Workspace",
        hint: "/",
        icon: "dashboard",
        keywords: "workspace home dashboard",
        onSelect: () => navigate("/"),
      },
      {
        id: "route:cases",
        kind: "route",
        label: "Cases",
        hint: "/workspace/compass",
        icon: "folder-close",
        onSelect: () => navigate("/workspace/compass"),
      },
      {
        id: "route:cases-flagged",
        kind: "route",
        label: "Flagged cases",
        hint: "/workspace/compass?flagged=true",
        icon: "flag",
        keywords: "flagged priority",
        onSelect: () => navigate("/workspace/compass?flagged=true"),
      },
      {
        id: "route:cases-open",
        kind: "route",
        label: "Open cases",
        hint: "/workspace/compass?status=open",
        icon: "folder-open",
        keywords: "open active",
        onSelect: () => navigate("/workspace/compass?status=open"),
      },
    ];

    const actions: Command[] = [
      {
        id: "action:refresh",
        kind: "action",
        label: "Refresh data",
        hint: "Re-fetch all queries from API",
        icon: "refresh",
        keywords: "reload refetch",
        onSelect: async () => {
          await queryClient.invalidateQueries();
        },
      },
      {
        id: "action:signout",
        kind: "action",
        label: "Sign out",
        hint: "End the current session",
        icon: "log-out",
        keywords: "logout exit",
        onSelect: async () => {
          await signOut();
          navigate("/login", { replace: true });
        },
      },
    ];

    const caseCommands: Command[] = cases.map((c) => ({
      id: `case:${c.id}`,
      kind: "case",
      label: c.title,
      hint: `${c.caseNumber} · ${c.leadDetective ?? "no lead"}`,
      icon: c.flagged ? "flag" : "folder-close",
      keywords: `${c.caseNumber} ${c.title} ${c.leadDetective ?? ""} ${c.status}`,
      onSelect: () => navigate(`/cases/${c.id}`),
    }));

    return [...routes, ...actions, ...caseCommands];
  }, [cases, navigate, queryClient, signOut]);

  // Filter to matches (text + optional kind), then group by kind in KIND_ORDER.
  const filtered = useMemo(
    () =>
      commands.filter(
        (c) => matchesQuery(c, query) && (kindFilter === null || c.kind === kindFilter),
      ),
    [commands, query, kindFilter],
  );
  const grouped = useMemo(() => {
    const g: Record<CommandKind, Command[]> = { route: [], action: [], case: [] };
    for (const c of filtered) g[c.kind].push(c);
    return g;
  }, [filtered]);

  const select = useCallback(
    async (cmd: Command) => {
      pushRecent(cmd.label);
      close();
      await cmd.onSelect();
    },
    [close],
  );

  // In-dialog keyboard interactions: ↑/↓ move within the filtered list, Enter
  // selects, Esc closes. These are local to the open dialog (the listener only
  // mounts while `isOpen`), so they stay a scoped listener rather than Blueprint
  // global hotkeys — single keys like Esc/arrows shouldn't be app-wide combos.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "ArrowDown") {
        ev.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
      } else if (ev.key === "ArrowUp") {
        ev.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (ev.key === "Enter") {
        ev.preventDefault();
        const cmd = filtered[activeIndex];
        if (cmd) void select(cmd);
      } else if (ev.key === "Escape") {
        ev.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, filtered, activeIndex, select, close]);

  if (!isOpen) return null;

  // Initial state = empty query AND no active pill → show "Recent searches"
  // (the reference's clean empty state). Otherwise show grouped/filtered results.
  const isInitialState = query.trim() === "" && kindFilter === null;
  const recentTerms = recent.length > 0 ? recent : RECENT_FALLBACK;

  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPod|iPad/i.test(navigator.platform);
  const openKeys = isMac ? ["⌘", "J"] : ["Ctrl", "J"];

  return (
    <QuicksearchDialog data-testid="command-palette"
      open={isOpen}
      onClose={close}
      footer={
        <QuicksearchFooter
          hotkeys={[
            { keys: ["↑", "↓"], label: "Navigate" },
            { keys: ["Enter"], label: "Select" },
            { keys: ["Esc"], label: "Close" },
            { keys: openKeys, label: "Open Quicksearch" },
          ]}
        />
      }
    >
      <QuicksearchInput
        value={query}
        onChange={(v) => {
          setQuery(v);
          setActiveIndex(0);
        }}
        onClose={close}
        placeholder="Search cases, jump to a page, or run an action…"
        autoFocus
      />
      <QuicksearchFilters>
        {FILTER_PILLS.map((pill) => (
          <QuicksearchCategoryTag
            key={pill.kind}
            active={kindFilter === pill.kind}
            onClick={() => {
              setKindFilter((cur) => (cur === pill.kind ? null : pill.kind));
              setActiveIndex(0);
            }}
          >
            {pill.label}
          </QuicksearchCategoryTag>
        ))}
      </QuicksearchFilters>
      {isInitialState ? (
        <QuicksearchSection title="Recent searches">
          {recentTerms.map((term) => (
            <QuicksearchResultItem
              key={term}
              icon="search"
              onClick={() => {
                setQuery(term);
                setActiveIndex(0);
              }}
              label={
                <span className="bp-quicksearch-row">
                  <span className="bp-quicksearch-row-label">{term}</span>
                </span>
              }
            />
          ))}
        </QuicksearchSection>
      ) : filtered.length === 0 ? (
        <div className="bp-quicksearch-empty">
          <Icon icon="search" />
          <div>No matches for &ldquo;{query}&rdquo;</div>
        </div>
      ) : (
        KIND_ORDER.map((kind) => {
          const items = grouped[kind];
          if (items.length === 0) return null;
          return (
            <QuicksearchSection
              key={kind}
              title={
                <>
                  {KIND_LABEL[kind]} <Tag minimal round>{items.length}</Tag>
                </>
              }
            >
              {items.map((cmd) => {
                const flatIndex = filtered.indexOf(cmd);
                return (
                  <QuicksearchResultItem
                    key={cmd.id}
                    dataTestId={`command-palette-item-${cmd.id}`}
                    icon={cmd.icon}
                    onClick={() => select(cmd)}
                    label={
                      <span
                        className={`bp-quicksearch-row${flatIndex === activeIndex ? " is-active" : ""}`}
                      >
                        <span className="bp-quicksearch-row-label">{cmd.label}</span>
                        {cmd.hint && (
                          <span className="bp-quicksearch-row-hint">{cmd.hint}</span>
                        )}
                      </span>
                    }
                  />
                );
              })}
            </QuicksearchSection>
          );
        })
      )}
    </QuicksearchDialog>
  );
}

function matchesQuery(cmd: Command, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = `${cmd.label} ${cmd.hint ?? ""} ${cmd.keywords ?? ""}`.toLowerCase();
  return q.split(/\s+/).every((tok) => hay.includes(tok));
}

// Small inline keycombo hint for the header trigger. Same export as before
// so AppShell / RailLink don't need to change.
export function CmdKHint(): ReactElement {
  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPod|iPad/i.test(navigator.platform);
  return <Tag minimal>{isMac ? "⌘J" : "Ctrl+J"}</Tag>;
}
